import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';
import { api } from '../lib/api';
import { formatPriceK } from '../lib/price';
import type { MenuResponse } from '../lib/types';

type QrSection = 'home' | 'guide' | 'whisky' | 'cocktail' | 'custom';
type GuideSub = 'guide' | 'delivery';
type WhiskySub = 'scotch' | 'american' | 'other';
type Lang = 'ko' | 'en';
type Strength = 'none' | 'light' | 'medium' | 'strong';
type Addon = 'none' | 'sparkling' | 'milk';
type QrItem = MenuResponse['categories'][number]['items'][number] & { category_name: string };
type FlavorKey =
  | 'lemon_lime'
  | 'orange'
  | 'peach'
  | 'herb'
  | 'berry'
  | 'melon'
  | 'coconut'
  | 'pineapple'
  | 'wood_smoke'
  | 'honey'
  | 'ginger'
  | 'coffee'
  | 'cinnamon'
  | 'chocolate';

const TXT = {
  ko: {
    qrTitle: '타로맨스 QR 메뉴',
    selectPrompt: '원하시는 메뉴를 선택해 주세요.',
    guide: '이용안내',
    guideGuide: '이용안내',
    guideDelivery: '배달추천',
    whisky: '위스키',
    whiskyScotch: '스카치',
    whiskyAmerican: '아메리칸',
    whiskyOther: '기타',
    cocktail: '칵테일',
    custom: '커스텀 칵테일',
    noItems: '표시할 항목이 없습니다.',
    preparing: '커스텀 칵테일 입력 폼입니다.',
    navLabel: 'QR 페이지 이동',
    language: '언어',
    korean: '한국어',
    english: 'English',
    alcohol: '알콜도수',
    sweetness: '단맛',
    sourness: '신맛',
    extra: '탄산 / 우유 추가',
    none: '없음',
    light: '약하게',
    medium: '적당히',
    strong: '강하게',
    sparkling: '탄산',
    milk: '우유&크림',
    flavorPref: '선호하는 맛 / 향 (3개 이하)',
    flavorShort: '맛 / 향',
    reset: '리셋',
    order: '주문',
    summaryTitle: '주문 내용 확인',
    close: '닫기',
    orderGuide: '주문을 위해,\n이 내용을 바텐더에게 보여주세요.',
    maxFlavorHint: '최대 3개까지 선택 가능합니다.',
    customNotice: '커스텀 칵테일은 25,000 원 고정입니다.',
    soldOut: 'SOLD',
    detailView: '상세보기',
    detailClose: '닫기',
    detailInfo: '세부 정보',
    simpleRecipe: '재료',
    story: '스토리',
    noImage: '등록된 이미지가 없습니다.',
    glassShort: 'G',
    bottleShort: 'B'
  },
  en: {
    qrTitle: 'TAROMANCE QR MENU',
    selectPrompt: 'Choose a section.',
    guide: 'GUIDELINE',
    guideGuide: 'Guide',
    guideDelivery: 'Delivery',
    whisky: 'WHISKY',
    whiskyScotch: 'Scotch',
    whiskyAmerican: 'American',
    whiskyOther: 'Others',
    cocktail: 'COCKTAIL',
    custom: 'CUSTOM COCKTAIL',
    noItems: 'No items to display.',
    preparing: 'Custom cocktail order form.',
    navLabel: 'QR section navigation',
    language: 'Language',
    korean: 'Korean',
    english: 'English',
    alcohol: 'Alcohol Level',
    sweetness: 'Sweetness',
    sourness: 'Sourness',
    extra: 'Sparkling / Milk',
    none: 'None',
    light: 'Light',
    medium: 'Medium',
    strong: 'Strong',
    sparkling: 'Sparkling',
    milk: 'Milk & Cream',
    flavorPref: 'Preferred Flavor / Aroma (up to 3)',
    flavorShort: 'Flavor / Aroma',
    reset: 'Reset',
    order: 'Order',
    summaryTitle: 'Order Summary',
    close: 'Close',
    orderGuide: 'For ordering,\nplease show this to the bartender.',
    maxFlavorHint: 'You can choose up to 3.',
    customNotice: 'Custom cocktail price starts from 19,000 KRW.\nPrice increases by alcohol level and extra ingredients.',
    soldOut: 'SOLD',
    detailView: 'Details',
    detailClose: 'Close',
    detailInfo: 'Details',
    simpleRecipe: 'Ingredients',
    story: 'Story',
    noImage: 'No image available.',
    glassShort: 'G',
    bottleShort: 'B'
  }
} as const;

const FLAVOR_KEYS: FlavorKey[] = [
  'lemon_lime',
  'orange',
  'peach',
  'herb',
  'berry',
  'melon',
  'coconut',
  'pineapple',
  'wood_smoke',
  'honey',
  'ginger',
  'coffee',
  'cinnamon',
  'chocolate'
];

const FLAVOR_LABELS: Record<FlavorKey, { ko: string; en: string }> = {
  lemon_lime: { ko: '레몬/라임', en: 'Lemon/Lime' },
  orange: { ko: '오렌지', en: 'Orange' },
  peach: { ko: '복숭아', en: 'Peach' },
  herb: { ko: '허브', en: 'Herb' },
  berry: { ko: '베리류', en: 'Berry' },
  melon: { ko: '멜론', en: 'Melon' },
  coconut: { ko: '코코넛', en: 'Coconut' },
  pineapple: { ko: '파인애플', en: 'Pineapple' },
  wood_smoke: { ko: '목재/훈연', en: 'Wood/Smoke' },
  honey: { ko: '꿀', en: 'Honey' },
  ginger: { ko: '생강', en: 'Ginger' },
  coffee: { ko: '커피', en: 'Coffee' },
  cinnamon: { ko: '시나몬', en: 'Cinnamon' },
  chocolate: { ko: '초콜릿', en: 'Chocolate' }
};

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/['"`]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getLocalImagePath(item: QrItem): string | null {
  const baseName = toSlug(item.name_en ?? item.name ?? '');
  if (!baseName) return null;

  const category = item.category_name.trim().toLowerCase();
  if (item.source === 'cocktail') {
    let folder = 'Signature';
    if (category === 'classic') folder = 'Classic';
    if (category === 'highball') folder = 'Highball';
    return `/images/menu/DB_Cocktail/${folder}/${baseName}.png`;
  }

  if (item.source === 'spirits') {
    if (category === 'scotch') return `/images/menu/DB_Spirits/Whisky/Scotch/${baseName}.png`;
    if (category === 'american') return `/images/menu/DB_Spirits/Whisky/American/${baseName}.png`;
    if (category === 'other country' || category === 'others') {
      return `/images/menu/DB_Spirits/Whisky/Others/${baseName}.png`;
    }
    if (category === 'wine') return `/images/menu/DB_Spirits/Wine/${baseName}.png`;
    return `/images/menu/DB_Spirits/Spirits/${baseName}.png`;
  }

  return null;
}

export function QrPage() {
  const [data, setData] = useState<MenuResponse | null>(null);
  const [section, setSection] = useState<QrSection>('home');
  const [lang, setLang] = useState<Lang>('ko');
  const [langOpen, setLangOpen] = useState(false);
  const [guideSub, setGuideSub] = useState<GuideSub>('guide');
  const [whiskySub, setWhiskySub] = useState<WhiskySub>('scotch');
  const [alcohol, setAlcohol] = useState<Strength>('none');
  const [sweetness, setSweetness] = useState<Strength>('none');
  const [sourness, setSourness] = useState<Strength>('none');
  const [extra, setExtra] = useState<Addon>('none');
  const [flavors, setFlavors] = useState<FlavorKey[]>([]);
  const [orderOpen, setOrderOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<QrItem | null>(null);
  const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>({});

  const t = TXT[lang];

  useEffect(() => {
    api.getPublicMenu('qr').then((res) => {
      setData(res);
    });
  }, []);

  function resetPageScrollTop() {
    const active = document.activeElement as HTMLElement | null;
    active?.blur?.();

    const scrollingEl = document.scrollingElement as HTMLElement | null;
    if (scrollingEl) scrollingEl.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function forcePageTop() {
    resetPageScrollTop();
    window.requestAnimationFrame(() => resetPageScrollTop());
    window.setTimeout(() => resetPageScrollTop(), 50);
  }

  function changeSection(next: QrSection) {
    setSection(next);
    setLangOpen(false);
    forcePageTop();
  }

  function changeGuideSub(next: GuideSub) {
    setGuideSub(next);
    forcePageTop();
  }

  function changeWhiskySub(next: WhiskySub) {
    setWhiskySub(next);
    forcePageTop();
  }

  useLayoutEffect(() => {
    if (section === 'home') return;

    resetPageScrollTop();
    const raf = window.requestAnimationFrame(() => resetPageScrollTop());
    const t1 = window.setTimeout(() => resetPageScrollTop(), 60);
    const t2 = window.setTimeout(() => resetPageScrollTop(), 160);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [section, guideSub, whiskySub]);

  const allItems = useMemo(() => {
    if (!data) return [];
    return data.categories.flatMap((category) =>
      category.items.map((item) => ({
        ...item,
        category_name: category.name
      }))
    );
  }, [data]);

  const whiskyItems = useMemo(() => {
    const filtered = allItems.filter((item) => item.source === 'spirits' && !item.is_spacer);
    if (whiskySub === 'scotch') {
      return filtered.filter((item) => item.category_name.trim().toLowerCase() === 'scotch');
    }
    if (whiskySub === 'american') {
      return filtered.filter((item) => item.category_name.trim().toLowerCase() === 'american');
    }
    return filtered.filter((item) => ['other country', 'others'].includes(item.category_name.trim().toLowerCase()));
  }, [allItems, whiskySub]);

  const cocktailItems = useMemo(
    () =>
      allItems.filter(
        (item) =>
          item.source === 'cocktail' &&
          ['signature', 'classic'].includes(item.category_name.trim().toLowerCase()) &&
          !item.is_spacer
      ),
    [allItems]
  );

  const guideItems = useMemo(() => {
    const banners = data?.banners ?? [];
    return banners.filter((banner) => {
      const category = (banner.category ?? '').trim().toLowerCase();
      if (guideSub === 'guide') return category === 'guide';
      return category === 'deliveryfood';
    });
  }, [data, guideSub]);

  function optionLabel(value: Strength | Addon): string {
    if (value === 'none') return t.none;
    if (value === 'light') return t.light;
    if (value === 'medium') return t.medium;
    if (value === 'strong') return t.strong;
    if (value === 'sparkling') return t.sparkling;
    return t.milk;
  }

  function optionToneClass(value: Strength | Addon): string {
    if (value === 'none') return 'tone-none';
    if (value === 'light') return 'tone-light';
    if (value === 'medium') return 'tone-medium';
    if (value === 'strong') return 'tone-strong';
    if (value === 'sparkling') return 'tone-sparkling';
    return 'tone-milk';
  }

  function strengthLevel(value: Strength): number {
    if (value === 'none') return 0;
    if (value === 'light') return 1;
    if (value === 'medium') return 2;
    return 3;
  }

  function renderStrengthValue(value: Strength) {
    const level = strengthLevel(value);
    return (
      <span className="qr-order-option-wrap">
        <strong className={optionToneClass(value)}>{optionLabel(value)}</strong>
        <span className="qr-option-stars" aria-label={`${level}/3`}>
          {[0, 1, 2].map((idx) => (
            <span key={idx} className={idx < level ? 'filled' : 'empty'}>
              {idx < level ? '★' : '☆'}
            </span>
          ))}
        </span>
      </span>
    );
  }

  function flavorToneClass(flavor: FlavorKey): string {
    const map: Record<FlavorKey, string> = {
      lemon_lime: 'flavor-lemon',
      orange: 'flavor-orange',
      peach: 'flavor-peach',
      herb: 'flavor-herb',
      berry: 'flavor-berry',
      melon: 'flavor-melon',
      coconut: 'flavor-coconut',
      pineapple: 'flavor-pineapple',
      wood_smoke: 'flavor-wood',
      honey: 'flavor-honey',
      ginger: 'flavor-ginger',
      coffee: 'flavor-coffee',
      cinnamon: 'flavor-cinnamon',
      chocolate: 'flavor-chocolate'
    };
    return map[flavor];
  }

  function sectionLabel(target: Exclude<QrSection, 'home'>): string {
    if (target === 'guide') return t.guide;
    if (target === 'whisky') return t.whisky;
    if (target === 'cocktail') return t.cocktail;
    return t.custom;
  }

  function navTabLabel(target: Exclude<QrSection, 'home'>): string {
    if (lang === 'en') {
      if (target === 'guide') return 'GUIDELINE';
      if (target === 'whisky') return 'WHISKY';
      if (target === 'cocktail') return 'COCKTAIL';
      return 'CUSTOM';
    }
    return sectionLabel(target);
  }

  function guideSubLabel(target: GuideSub): string {
    return target === 'guide' ? t.guideGuide : t.guideDelivery;
  }

  function whiskySubLabel(target: WhiskySub): string {
    if (target === 'scotch') return t.whiskyScotch;
    if (target === 'american') return t.whiskyAmerican;
    return t.whiskyOther;
  }

  function toggleFlavor(target: FlavorKey) {
    setFlavors((prev) => {
      if (prev.includes(target)) return prev.filter((v) => v !== target);
      if (prev.length >= 3) return prev;
      return [...prev, target];
    });
  }

  function resetCustom() {
    setAlcohol('none');
    setSweetness('none');
    setSourness('none');
    setExtra('none');
    setFlavors([]);
  }

  function normalizeStory(value: string | null | undefined): string | null {
    if (!value) return null;
    return value.replace(/\\n/g, '\n');
  }

  function displayName(item: QrItem): string {
    return lang === 'en' ? (item.name_en ?? item.name) : item.name;
  }

  function imageSrc(item: QrItem): string | null {
    return getLocalImagePath(item) ?? item.image_url;
  }

  function imageKey(item: QrItem): string {
    return `${item.id}:${imageSrc(item) ?? 'none'}`;
  }

  function onImageError(item: QrItem) {
    const key = imageKey(item);
    setImageErrorMap((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  }

  function renderItemImage(item: QrItem) {
    const src = imageSrc(item);
    const key = imageKey(item);
    const failed = !src || imageErrorMap[key];

    return (
      <div className="qr-item-image-frame">
        {!failed ? (
          <img loading="lazy" src={src} alt={displayName(item)} onError={() => onImageError(item)} />
        ) : (
          <div className="qr-item-image-fallback">[이미지 준비중]</div>
        )}
      </div>
    );
  }

  function renderMenuItems(items: QrItem[], kind: 'whisky' | 'cocktail') {
    if (!items.length) {
      return (
        <article className="card compact-item">
          <p>{t.noItems}</p>
        </article>
      );
    }

    return items.map((item) => {
      const soldOut30 = item.soldout_30ml ?? item.is_soldout;
      const soldOutBottle = item.soldout_bottle ?? item.is_soldout;
      const fullySoldOut = item.source === 'spirits' ? soldOut30 && soldOutBottle : item.is_soldout;

      if (item.is_title) {
        return (
          <article key={item.id} className="compact-item title-item qr-title-item">
            <h3>{displayName(item)}</h3>
            {item.description ? <p className="qr-multiline">{item.description}</p> : null}
          </article>
        );
      }

      if (kind === 'whisky') {
        return (
          <details key={item.id} className="card compact-item">
            <summary className="qr-summary-whisky">
              <span className="qr-whisky-main">
                <span className={fullySoldOut ? 'sold-strike' : ''}>{displayName(item)}</span>
                {item.description ? <small>{item.description}</small> : null}
              </span>
              <span className="qr-whisky-prices">
                <strong>{t.glassShort} : {soldOut30 ? t.soldOut : formatPriceK(item.price_30ml ?? 0)}</strong>
                <strong>{t.bottleShort} : {soldOutBottle ? t.soldOut : formatPriceK(item.price_bottle ?? 0)}</strong>
              </span>
            </summary>
            {renderItemImage(item)}
            {item.flavour_character ? <p className="qr-flavour-line">{item.flavour_character}</p> : null}
            <button type="button" className="btn qr-detail-btn" onClick={() => setDetailItem(item)}>
              {t.detailView}
            </button>
          </details>
        );
      }

      return (
        <details key={item.id} className="card compact-item">
          <summary className="qr-summary-cocktail">
            <span>{displayName(item)}</span>
            <strong>{formatPriceK(item.price)}</strong>
          </summary>
          {renderItemImage(item)}
          {item.description ? <p>{item.description}</p> : null}
          {fullySoldOut ? <span className="badge soldout">{t.soldOut}</span> : null}
          <button type="button" className="btn qr-detail-btn" onClick={() => setDetailItem(item)}>
            {t.detailView}
          </button>
        </details>
      );
    });
  }

  return (
    <section className="stack-md qr-page qr-mobile">
      <Seo title="타로맨스 | QR 메뉴" description="손님용 QR 메뉴" />
      {!data && <p>불러오는 중...</p>}

      {data && (
        <div className="qr-shell">
          <header className="qr-topbar">
            <button type="button" className="qr-top-title" onClick={() => changeSection('home')}>
              {t.qrTitle}
            </button>
            <div className="qr-lang-wrap">
              <button type="button" className="qr-lang-btn" onClick={() => setLangOpen((v) => !v)}>
                Language
              </button>
              {langOpen ? (
                <div className="qr-lang-menu">
                  <button type="button" onClick={() => { setLang('ko'); setLangOpen(false); }} className={lang === 'ko' ? 'active' : ''}>
                    {t.korean}
                  </button>
                  <button type="button" onClick={() => { setLang('en'); setLangOpen(false); }} className={lang === 'en' ? 'active' : ''}>
                    {t.english}
                  </button>
                </div>
              ) : null}
            </div>
          </header>

          {section === 'home' ? (
            <>
              <header className="qr-home-head">
                <p>{t.selectPrompt}</p>
              </header>
              <div className="qr-home-grid">
                <button type="button" className="qr-entry-btn" onClick={() => changeSection('guide')}>
                  {t.guide}
                </button>
                <button type="button" className="qr-entry-btn" onClick={() => changeSection('whisky')}>
                  {t.whisky}
                </button>
                <button type="button" className="qr-entry-btn" onClick={() => changeSection('cocktail')}>
                  {t.cocktail}
                </button>
                <button type="button" className="qr-entry-btn" onClick={() => changeSection('custom')}>
                  {t.custom}
                </button>
              </div>
            </>
          ) : (
            <>
              <header className="qr-detail-nav">
                <nav className="qr-nav-tabs en-grid" aria-label={t.navLabel}>
                  {(['guide', 'whisky', 'cocktail', 'custom'] as Array<Exclude<QrSection, 'home'>>).map((target) => (
                    <button
                      key={target}
                      type="button"
                      className={section === target ? 'pill active' : 'pill'}
                      onClick={() => changeSection(target)}
                    >
                      {navTabLabel(target)}
                    </button>
                  ))}
                </nav>
                {section === 'guide' ? (
                  <nav className="qr-subtabs" aria-label="guide sub navigation">
                    {(['guide', 'delivery'] as GuideSub[]).map((target) => (
                      <button
                        key={target}
                        type="button"
                        className={guideSub === target ? 'pill active' : 'pill'}
                        onClick={() => changeGuideSub(target)}
                      >
                        {guideSubLabel(target)}
                      </button>
                    ))}
                  </nav>
                ) : null}
                {section === 'whisky' ? (
                  <nav className="qr-subtabs" aria-label="whisky sub navigation">
                    {(['scotch', 'american', 'other'] as WhiskySub[]).map((target) => (
                      <button
                        key={target}
                        type="button"
                        className={whiskySub === target ? 'pill active' : 'pill'}
                        onClick={() => changeWhiskySub(target)}
                      >
                        {whiskySubLabel(target)}
                      </button>
                    ))}
                  </nav>
                ) : null}
              </header>
              <div className="stack-sm">
                {section === 'guide' &&
                  (guideItems.length ? (
                    guideItems.map((banner) => (
                      <article key={banner.id} className="card compact-item">
                        <h3>{lang === 'en' ? (banner.title_en ?? banner.title) : (banner.title_ko ?? banner.title)}</h3>
                        {(lang === 'en' ? (banner.body_en ?? banner.body) : (banner.body_ko ?? banner.body)) ? (
                          <p className="qr-multiline">{lang === 'en' ? (banner.body_en ?? banner.body) : (banner.body_ko ?? banner.body)}</p>
                        ) : null}
                      </article>
                    ))
                  ) : (
                    <article className="card compact-item">
                      <p>{t.noItems}</p>
                    </article>
                  ))}

                {section === 'whisky' && renderMenuItems(whiskyItems as QrItem[], 'whisky')}
                {section === 'cocktail' && renderMenuItems(cocktailItems as QrItem[], 'cocktail')}

                {section === 'custom' && (
                  <article className="card compact-item qr-custom-card">
                    <fieldset className="qr-form-group">
                      <legend>{t.alcohol}</legend>
                      <div className="qr-radio-grid">
                        {(['none', 'light', 'medium', 'strong'] as Strength[]).map((value) => (
                          <label key={value}>
                            <input type="radio" name="alcohol" checked={alcohol === value} onChange={() => setAlcohol(value)} />
                            <span>{optionLabel(value)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="qr-form-group">
                      <legend>{t.sweetness}</legend>
                      <div className="qr-radio-grid">
                        {(['none', 'light', 'medium', 'strong'] as Strength[]).map((value) => (
                          <label key={value}>
                            <input type="radio" name="sweetness" checked={sweetness === value} onChange={() => setSweetness(value)} />
                            <span>{optionLabel(value)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="qr-form-group">
                      <legend>{t.sourness}</legend>
                      <div className="qr-radio-grid">
                        {(['none', 'light', 'medium', 'strong'] as Strength[]).map((value) => (
                          <label key={value}>
                            <input type="radio" name="sourness" checked={sourness === value} onChange={() => setSourness(value)} />
                            <span>{optionLabel(value)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="qr-form-group">
                      <legend>{t.extra}</legend>
                      <div className="qr-radio-grid">
                        {(['none', 'sparkling', 'milk'] as Addon[]).map((value) => (
                          <label key={value}>
                            <input type="radio" name="extra" checked={extra === value} onChange={() => setExtra(value)} />
                            <span>{optionLabel(value)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="qr-form-group">
                      <legend>{t.flavorPref}</legend>
                      <div className="qr-check-grid">
                        {FLAVOR_KEYS.map((flavor) => (
                          <label key={flavor}>
                            <input type="checkbox" checked={flavors.includes(flavor)} onChange={() => toggleFlavor(flavor)} />
                            <span>{FLAVOR_LABELS[flavor][lang]}</span>
                          </label>
                        ))}
                      </div>
                      <small>{t.maxFlavorHint}</small>
                    </fieldset>

                    <div className="qr-form-actions">
                      <button type="button" className="btn" onClick={resetCustom}>
                        {t.reset}
                      </button>
                      <button type="button" className="btn active" onClick={() => setOrderOpen(true)}>
                        {t.order}
                      </button>
                    </div>

                    <p className="qr-custom-notice">{t.customNotice}</p>
                  </article>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {orderOpen ? (
        <aside className="qr-order-modal" role="dialog" aria-modal="true">
          <div className="qr-order-card">
            <h3>{t.summaryTitle}</h3>
            <p>{t.alcohol}: {renderStrengthValue(alcohol)}</p>
            <p>{t.sweetness}: {renderStrengthValue(sweetness)}</p>
            <p>{t.sourness}: {renderStrengthValue(sourness)}</p>
            <p>{t.extra}: <strong className={optionToneClass(extra)}>{optionLabel(extra)}</strong></p>
            <p>
              {t.flavorShort}:{' '}
              {flavors.length ? (
                <span className="qr-flavor-tags">
                  {flavors.map((f) => (
                    <strong key={f} className={flavorToneClass(f)}>
                      {FLAVOR_LABELS[f][lang]}
                    </strong>
                  ))}
                </span>
              ) : (
                <strong className={optionToneClass('none')}>{t.none}</strong>
              )}
            </p>
            <p className="qr-order-note">{t.orderGuide}</p>
            <button type="button" className="btn active" onClick={() => setOrderOpen(false)}>
              {t.close}
            </button>
          </div>
        </aside>
      ) : null}

      {detailItem ? (
        <aside className="qr-order-modal" role="dialog" aria-modal="true">
          <div className="qr-order-card qr-detail-card">
            <button type="button" className="qr-popup-close" onClick={() => setDetailItem(null)}>
              {t.detailClose}
            </button>
            <h3>{displayName(detailItem)}</h3>
            {imageSrc(detailItem) && !imageErrorMap[imageKey(detailItem)] ? (
              <img loading="lazy" src={imageSrc(detailItem) ?? ''} alt={displayName(detailItem)} onError={() => onImageError(detailItem)} />
            ) : (
              <p>[이미지 준비중]</p>
            )}
            {detailItem.source === 'cocktail' ? (
              <>
                {detailItem.description ? <p><strong>{t.simpleRecipe}</strong><br />{detailItem.description}</p> : null}
                {normalizeStory(lang === 'ko' ? detailItem.story_k : detailItem.story_e) ? (
                  <div className="qr-story-scroll">
                    <strong>{t.story}</strong>
                    <p className="qr-multiline">{normalizeStory(lang === 'ko' ? detailItem.story_k : detailItem.story_e)}</p>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <p><strong>{t.detailInfo}</strong></p>
                {detailItem.description ? <p>{detailItem.description}</p> : null}
                {detailItem.flavour_character ? <p className="qr-multiline">{detailItem.flavour_character}</p> : null}
                {detailItem.abv ? <p>ABV: {detailItem.abv}</p> : null}
                {detailItem.tags?.length ? <p>{detailItem.tags.join(', ')}</p> : null}
              </>
            )}
          </div>
        </aside>
      ) : null}
    </section>
  );
}
